import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Spinner } from 'react-bootstrap';
import { Trash2, AlertTriangle, ShieldAlert } from 'lucide-react';
import apiClient from '../../api';
import useAuthStore from '../../store/useAuthStore';
import { toast } from 'react-toastify';

const DeleteAccountModal = ({ show, onHide }) => {
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const { logout } = useAuthStore();

  const handleReset = () => {
    setReason('');
    setOtherReason('');
    setConfirmText('');
    setErrorMessage(null);
    setLoading(false);
  };

  const handleClose = () => {
    handleReset();
    onHide();
  };

  const isFormValid =
    reason !== '' &&
    (reason !== 'Otro motivo' || otherReason.trim().length > 0) &&
    confirmText.trim().toUpperCase() === 'ELIMINAR';

  const handleDeleteAccount = async () => {
    if (!isFormValid || loading) return;

    setLoading(true);
    setErrorMessage(null);

    const finalReason = reason === 'Otro motivo' ? `Otro: ${otherReason}` : reason;

    try {
      await apiClient.delete('/users/me', {
        data: { reason: finalReason }
      });

      toast.success("Tu cuenta ha sido eliminada y deshabilitada exitosamente.");
      handleClose();
      await logout();
      window.location.href = '/login';
    } catch (err) {
      console.error("Error al eliminar cuenta:", err);
      const detail = err.response?.data?.detail || "No se pudo procesar la eliminación de la cuenta.";
      setErrorMessage(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="bg-danger text-white py-2">
        <Modal.Title className="h6 mb-0 d-flex align-items-center gap-2">
          <Trash2 size={18} />
          Eliminar Mi Cuenta de Flujo App
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        {errorMessage ? (
          <Card className="border-danger bg-danger bg-opacity-10 shadow-sm">
            <Card.Body className="p-3 text-center">
              <ShieldAlert size={42} className="text-danger mb-2" />
              <h5 className="h6 text-danger fw-bold">Imposible Eliminar Cuenta en Este Momento</h5>
              <p className="small text-secondary mb-3">{errorMessage}</p>
              <Button variant="outline-danger" size="sm" onClick={() => setErrorMessage(null)} className="fw-bold">
                Entendido / Volver
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <>
            <Alert variant="warning" className="small py-2 px-3 d-flex align-items-center gap-2 mb-3">
              <AlertTriangle size={20} className="flex-shrink-0" />
              <div>
                <strong>Atención:</strong> Esta acción deshabilitará tu acceso a la aplicación de forma permanente.
              </div>
            </Alert>

            <Form.Group className="mb-3">
              <Form.Label className="small fw-bold">1. ¿Por qué deseas eliminar tu cuenta?</Form.Label>
              <div className="ps-1">
                {[
                  'Ya no realizo servicios con la empresa',
                  'Tengo problemas técnicos con la aplicación',
                  'No deseo seguir utilizando la plataforma',
                  'Otro motivo'
                ].map((opt, i) => (
                  <Form.Check
                    key={i}
                    type="radio"
                    id={`delete-reason-${i}`}
                    label={<span className="small">{opt}</span>}
                    name="deleteReason"
                    value={opt}
                    checked={reason === opt}
                    onChange={(e) => setReason(e.target.value)}
                    className="mb-1"
                  />
                ))}
              </div>
              {reason === 'Otro motivo' && (
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Escribe el motivo..."
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  className="mt-2"
                />
              )}
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold text-danger">
                2. Para confirmar, escribe la palabra "ELIMINAR" a continuación:
              </Form.Label>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Escribe ELIMINAR aquí"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="fw-bold text-danger"
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light py-2">
        <Button variant="secondary" size="sm" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {!errorMessage && (
          <Button
            variant="danger"
            size="sm"
            className="fw-bold d-flex align-items-center gap-1"
            disabled={!isFormValid || loading}
            onClick={handleDeleteAccount}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 size={15} />
                Confirmar Eliminación Definitiva
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteAccountModal;
